import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import WithdrawService from "../../services/withdraw.service";

/* =====================================================
   GET ALL WITHDRAWALS
===================================================== */

export const getAllWithdrawals = createAsyncThunk(
  "withdraw/getAllWithdrawals",
  async (params = {}, thunkAPI) => {
    try {
      const response =
        await WithdrawService.getAllWithdrawals(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch withdrawals"
      );
    }
  }
);

/* =====================================================
   GET SUPPORT INCOME
===================================================== */

export const getSupportIncome = createAsyncThunk(
  "withdraw/getSupportIncome",
  async (params = {}, thunkAPI) => {
    try {
      const response =
        await WithdrawService.getSupportIncome(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch support income"
      );
    }
  }
);

/* =====================================================
   GET ALL FUND TRANSFERS
===================================================== */

export const getAllFundTransfers = createAsyncThunk(
  "withdraw/getAllFundTransfers",
  async (params = {}, thunkAPI) => {
    try {
      const response =
        await WithdrawService.getAllFundTransfers(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch fund transfers"
      );
    }
  }
);

/* =====================================================
   GET ALL REFERRAL INCOME
===================================================== */

export const getAllReferralIncome = createAsyncThunk(
  "withdraw/getAllReferralIncome",
  async (params = {}, thunkAPI) => {
    try {
      const response =
        await WithdrawService.getAllReferralIncome(params);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch referral income"
      );
    }
  }
);

/* =====================================================
   APPROVE / REJECT WITHDRAWAL
===================================================== */

export const approveWithdrawal = createAsyncThunk(
  "withdraw/approveWithdrawal",
  async (payload, thunkAPI) => {
    try {
      const response =
        await WithdrawService.approveWithdrawal(payload);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to update withdrawal"
      );
    }
  }
);

/* =====================================================
   ADD SUPPORT INCOME
===================================================== */

export const addSupportIncome = createAsyncThunk(
  "withdraw/addSupportIncome",
  async (payload, thunkAPI) => {
    try {
      const response =
        await WithdrawService.addSupportIncome(payload);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to add support income"
      );
    }
  }
);

/* =====================================================
   INITIAL STATE
===================================================== */

const initialState = {
  /* ================= COMMON ================= */

  loading: false,
  error: null,

  /* ================= WITHDRAWALS ================= */

  withdrawals: [],
  withdrawalsLoading: false,
  withdrawalsError: null,

  /* ================= WITHDRAWAL SUMMARY ================= */

  summary: {
    totalRequests: 0,
    totalRequested: 0,
    totalApproved: 0,
    totalPending: 0,
    totalRejected: 0,
  },

  /* ================= WITHDRAWAL PAGINATION ================= */

  pagination: {
    total: 0,
    currentPage: 1,
    limit: 20,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },

  /* ================= APPROVE / REJECT ================= */

  approveLoading: false,
  approveSuccess: false,
  approveData: null,
  approveError: null,

  /* ================= REFERRAL INCOME ================= */

  referralIncome: [],
  referralIncomeLoading: false,
  referralIncomeError: null,

  referralIncomeSummary: {
    totalRecords: 0,
    totalAmount: 0,
  },

  referralIncomePagination: {
    total: 0,
    currentPage: 1,
    limit: 20,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },

  /* ================= SUPPORT INCOME ================= */

  supportIncome: [],
  supportIncomeLoading: false,
  supportIncomeError: null,

  supportIncomeTotalAmount: 0,

  supportIncomePagination: {
    totalRecords: 0,
    currentPage: 1,
    limit: 25,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },

  /* ================= FUND TRANSFERS ================= */

  fundTransfers: [],
  fundTransfersLoading: false,
  fundTransfersError: null,

  fundTransfersPagination: {
    total: 0,
    currentPage: 1,
    limit: 20,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
};

/* =====================================================
   SLICE
===================================================== */

const withdrawSlice = createSlice({
  name: "withdraw",

  initialState,

  reducers: {
    /* =================================================
       RESET APPROVE STATUS
    ================================================= */

    resetApproveStatus: (state) => {
      state.approveLoading = false;
      state.approveSuccess = false;
      state.approveData = null;
      state.approveError = null;
    },

    /* =================================================
       CLEAR WITHDRAWALS
    ================================================= */

    clearWithdrawals: (state) => {
      state.withdrawals = [];

      state.summary = {
        totalRequests: 0,
        totalRequested: 0,
        totalApproved: 0,
        totalPending: 0,
        totalRejected: 0,
      };

      state.pagination = {
        total: 0,
        currentPage: 1,
        limit: 20,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      };

      state.withdrawalsError = null;
    },

    /* =================================================
       CLEAR REFERRAL INCOME
    ================================================= */

    clearReferralIncome: (state) => {
      state.referralIncome = [];

      state.referralIncomeSummary = {
        totalRecords: 0,
        totalAmount: 0,
      };

      state.referralIncomePagination = {
        total: 0,
        currentPage: 1,
        limit: 20,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      };

      state.referralIncomeError = null;
    },

    /* =================================================
       CLEAR SUPPORT INCOME
    ================================================= */

    clearSupportIncome: (state) => {
      state.supportIncome = [];

      state.supportIncomeTotalAmount = 0;

      state.supportIncomePagination = {
        totalRecords: 0,
        currentPage: 1,
        limit: 25,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      };

      state.supportIncomeError = null;
    },

    /* =================================================
       CLEAR FUND TRANSFERS
    ================================================= */

    clearFundTransfers: (state) => {
      state.fundTransfers = [];

      state.fundTransfersPagination = {
        total: 0,
        currentPage: 1,
        limit: 20,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      };

      state.fundTransfersError = null;
    },

    /* =================================================
       CLEAR ERROR
    ================================================= */

    clearWithdrawError: (state) => {
      state.error = null;
      state.withdrawalsError = null;
      state.approveError = null;
      state.referralIncomeError = null;
      state.supportIncomeError = null;
      state.fundTransfersError = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* =====================================================
         GET ALL WITHDRAWALS
      ===================================================== */

      .addCase(getAllWithdrawals.pending, (state) => {
        state.withdrawalsLoading = true;
        state.withdrawalsError = null;
        state.error = null;
      })

      .addCase(getAllWithdrawals.fulfilled, (state, action) => {
        state.withdrawalsLoading = false;
        state.withdrawalsError = null;
        state.error = null;

        const data = action.payload?.data || {};

        /* ================= WITHDRAWALS ================= */

        state.withdrawals = Array.isArray(data.withdrawals)
          ? data.withdrawals
          : [];

        /* ================= SUMMARY ================= */

        state.summary = {
          totalRequests:
            Number(data.summary?.totalRequests) || 0,

          totalRequested:
            Number(data.summary?.totalRequested) || 0,

          totalApproved:
            Number(data.summary?.totalApproved) || 0,

          totalPending:
            Number(data.summary?.totalPending) || 0,

          totalRejected:
            Number(data.summary?.totalRejected) || 0,
        };

        /* ================= PAGINATION ================= */

        const pagination = data.pagination || {};

        const currentPage =
          Number(
            pagination.currentPage ??
            pagination.page
          ) || 1;

        const limit =
          Number(pagination.limit) || 20;

        const total =
          Number(
            pagination.total ??
            pagination.totalRecords
          ) || 0;

        const totalPages =
          Number(pagination.totalPages) ||
          (total > 0
            ? Math.ceil(total / limit)
            : 0);

        state.pagination = {
          total,
          currentPage,
          limit,
          totalPages,

          hasNextPage:
            pagination.hasNextPage ??
            currentPage < totalPages,

          hasPreviousPage:
            pagination.hasPreviousPage ??
            currentPage > 1,
        };
      })

      .addCase(getAllWithdrawals.rejected, (state, action) => {
        state.withdrawalsLoading = false;

        state.withdrawalsError =
          action.payload ||
          "Failed to fetch withdrawals";

        state.error =
          action.payload ||
          "Failed to fetch withdrawals";

        state.withdrawals = [];
      })

      /* =====================================================
         GET SUPPORT INCOME
      ===================================================== */

      .addCase(getSupportIncome.pending, (state) => {
        state.supportIncomeLoading = true;
        state.supportIncomeError = null;
        state.error = null;
      })

      .addCase(getSupportIncome.fulfilled, (state, action) => {
        state.supportIncomeLoading = false;
        state.supportIncomeError = null;
        state.error = null;

        const data = action.payload?.data || {};

        /* ================= RECORDS ================= */

        /*
          API RESPONSE:

          data: {
            records: [...]
          }
        */

        state.supportIncome = Array.isArray(
          data.records
        )
          ? data.records
          : [];

        /* ================= TOTAL AMOUNT ================= */

        state.supportIncomeTotalAmount =
          Number(data.totalAmount) || 0;

        /* ================= PAGINATION ================= */

        const pagination =
          data.pagination || {};

        const totalRecords =
          Number(
            pagination.totalRecords
          ) || 0;

        const currentPage =
          Number(
            pagination.currentPage
          ) || 1;

        const limit =
          Number(
            pagination.limit
          ) || 25;

        const totalPages =
          Number(
            pagination.totalPages
          ) ||
          (totalRecords > 0
            ? Math.ceil(
              totalRecords / limit
            )
            : 0);

        state.supportIncomePagination = {
          totalRecords,
          currentPage,
          limit,
          totalPages,

          hasNextPage:
            currentPage < totalPages,

          hasPreviousPage:
            currentPage > 1,
        };
      })

      .addCase(getSupportIncome.rejected, (state, action) => {
        state.supportIncomeLoading = false;

        state.supportIncomeError =
          action.payload ||
          "Failed to fetch support income";

        state.error =
          action.payload ||
          "Failed to fetch support income";

        state.supportIncome = [];

        state.supportIncomeTotalAmount = 0;

        state.supportIncomePagination = {
          totalRecords: 0,
          currentPage: 1,
          limit: 25,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      })

      /* =====================================================
         GET ALL FUND TRANSFERS
      ===================================================== */

      .addCase(getAllFundTransfers.pending, (state) => {
        state.fundTransfersLoading = true;
        state.fundTransfersError = null;
        state.error = null;
      })

      .addCase(getAllFundTransfers.fulfilled, (state, action) => {
        state.fundTransfersLoading = false;
        state.fundTransfersError = null;
        state.error = null;

        const data = action.payload?.data || {};

        /* ================= TRANSFERS ================= */

        state.fundTransfers =
          Array.isArray(data.fundTransfers)
            ? data.fundTransfers
            : Array.isArray(data.transfers)
              ? data.transfers
              : [];

        /* ================= PAGINATION ================= */

        const pagination =
          data.pagination || {};

        const currentPage =
          Number(
            pagination.currentPage ??
            pagination.page
          ) || 1;

        const limit =
          Number(
            pagination.limit
          ) || 20;

        const total =
          Number(
            pagination.total ??
            pagination.totalRecords
          ) || 0;

        const totalPages =
          Number(
            pagination.totalPages
          ) ||
          (total > 0
            ? Math.ceil(
              total / limit
            )
            : 0);

        state.fundTransfersPagination = {
          total,
          currentPage,
          limit,
          totalPages,

          hasNextPage:
            pagination.hasNextPage ??
            currentPage < totalPages,

          hasPreviousPage:
            pagination.hasPreviousPage ??
            currentPage > 1,
        };
      })

      .addCase(getAllFundTransfers.rejected, (state, action) => {
        state.fundTransfersLoading = false;

        state.fundTransfersError =
          action.payload ||
          "Failed to fetch fund transfers";

        state.error =
          action.payload ||
          "Failed to fetch fund transfers";

        state.fundTransfers = [];
      })

      /* =====================================================
         GET ALL REFERRAL INCOME
      ===================================================== */

      .addCase(getAllReferralIncome.pending, (state) => {
        state.referralIncomeLoading = true;
        state.referralIncomeError = null;
        state.error = null;
      })

      .addCase(getAllReferralIncome.fulfilled, (state, action) => {
        state.referralIncomeLoading = false;
        state.referralIncomeError = null;
        state.error = null;

        const data = action.payload?.data || {};

        /* ================= REFERRAL INCOME ================= */

        state.referralIncome =
          Array.isArray(
            data.referralIncome
          )
            ? data.referralIncome
            : Array.isArray(
              data.records
            )
              ? data.records
              : [];

        /* ================= SUMMARY ================= */

        state.referralIncomeSummary = {
          totalRecords:
            Number(
              data.summary?.totalRecords ??
              data.totalRecords
            ) || 0,

          totalAmount:
            Number(
              data.summary?.totalAmount ??
              data.totalAmount
            ) || 0,
        };

        /* ================= PAGINATION ================= */

        const pagination =
          data.pagination || {};

        const currentPage =
          Number(
            pagination.currentPage ??
            pagination.page
          ) || 1;

        const limit =
          Number(
            pagination.limit
          ) || 20;

        const total =
          Number(
            pagination.total ??
            pagination.totalRecords
          ) || 0;

        const totalPages =
          Number(
            pagination.totalPages
          ) ||
          (total > 0
            ? Math.ceil(
              total / limit
            )
            : 0);

        state.referralIncomePagination = {
          total,
          currentPage,
          limit,
          totalPages,

          hasNextPage:
            pagination.hasNextPage ??
            currentPage < totalPages,

          hasPreviousPage:
            pagination.hasPreviousPage ??
            currentPage > 1,
        };
      })

      .addCase(getAllReferralIncome.rejected, (state, action) => {
        state.referralIncomeLoading = false;

        state.referralIncomeError =
          action.payload ||
          "Failed to fetch referral income";

        state.error =
          action.payload ||
          "Failed to fetch referral income";

        state.referralIncome = [];

        state.referralIncomeSummary = {
          totalRecords: 0,
          totalAmount: 0,
        };

        state.referralIncomePagination = {
          total: 0,
          currentPage: 1,
          limit: 20,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      })

      /* =====================================================
         APPROVE / REJECT WITHDRAWAL
      ===================================================== */

      .addCase(approveWithdrawal.pending, (state) => {
        state.approveLoading = true;
        state.approveSuccess = false;
        state.approveData = null;
        state.approveError = null;
        state.error = null;
      })

      .addCase(approveWithdrawal.fulfilled, (state, action) => {
        state.approveLoading = false;
        state.approveSuccess = true;
        state.approveError = null;
        state.error = null;

        state.approveData =
          action.payload?.data || null;

        const updatedWithdrawal =
          action.payload?.data?.withdrawal ||
          action.payload?.data;

        if (updatedWithdrawal?._id) {
          state.withdrawals =
            state.withdrawals.map(
              (item) =>
                item?._id ===
                  updatedWithdrawal._id
                  ? {
                    ...item,
                    ...updatedWithdrawal,
                  }
                  : item
            );
        }
      })

      .addCase(approveWithdrawal.rejected, (state, action) => {
        state.approveLoading = false;
        state.approveSuccess = false;

        state.approveError =
          action.payload ||
          "Failed to update withdrawal";

        state.error =
          action.payload ||
          "Failed to update withdrawal";

        state.approveData = null;
      });
  },
});

/* =====================================================
   ACTIONS
===================================================== */

export const {
  resetApproveStatus,
  clearWithdrawals,
  clearReferralIncome,
  clearSupportIncome,
  clearFundTransfers,
  clearWithdrawError,
} = withdrawSlice.actions;

/* =====================================================
   REDUCER
===================================================== */

export default withdrawSlice.reducer;